import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  ArrowLeft, Globe, Settings, FileText, Mail, Shield, Lock,
  CheckCircle, XCircle, AlertTriangle, ChevronLeft, ChevronRight,
  ChevronUp, ChevronDown, ChevronsUpDown, ExternalLink
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';
import { formatDateARG, formatDateOnlyARG } from '../../lib/utils';

const DNSDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canRead } = usePermissions('assets', 'dns');
  
  const [source, setSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  
  // Records state
  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordsPage, setRecordsPage] = useState(1);
  const [recordsTotalPages, setRecordsTotalPages] = useState(1);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [recordsFilters, setRecordsFilters] = useState({ search: '', record_type: '' });
  const [recordsSortConfig, setRecordsSortConfig] = useState({ field: 'record_fqdn', dir: 'asc' });
  const [recordTypes, setRecordTypes] = useState([]);
  
  // Email Security state
  const [emailSecurity, setEmailSecurity] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);

  const fetchSource = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/assets/dns/sources/${id}`);
      setSource(response.data);
    } catch (error) {
      toast.error('Error al cargar dominio');
      navigate('/assets/dns');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecords = async () => {
    setRecordsLoading(true);
    try {
      const params = new URLSearchParams({
        page: recordsPage.toString(),
        page_size: '50',
        source_id: id,
        sort_by: recordsSortConfig.field,
        sort_dir: recordsSortConfig.dir
      });
      if (recordsFilters.search) params.append('search', recordsFilters.search);
      if (recordsFilters.record_type) params.append('record_type', recordsFilters.record_type);

      const response = await api.get(`/assets/dns/records?${params}`);
      setRecords(response.data.items);
      setRecordsTotalPages(response.data.pages);
      setRecordsTotal(response.data.total);
    } catch (error) {
      toast.error('Error al cargar records');
    } finally {
      setRecordsLoading(false);
    }
  };

  const fetchRecordTypes = async () => {
    try {
      const response = await api.get('/assets/dns/records/types');
      setRecordTypes(response.data);
    } catch (error) {
      console.error('Error fetching record types:', error);
    }
  };

  const fetchEmailSecurity = async () => {
    setEmailLoading(true);
    try {
      const response = await api.get(`/assets/dns/email-security/${id}`);
      setEmailSecurity(response.data);
    } catch (error) {
      // No email security data is OK
      setEmailSecurity(null);
    } finally {
      setEmailLoading(false);
    }
  };

  useEffect(() => {
    fetchSource();
    fetchRecordTypes();
  }, [id]);

  useEffect(() => {
    if (activeTab === 'records') {
      fetchRecords();
    } else if (activeTab === 'email') {
      fetchEmailSecurity();
    }
  }, [activeTab, recordsPage, recordsFilters, recordsSortConfig]);

  const handleRecordsFilterChange = (field, value) => {
    setRecordsFilters(prev => ({ ...prev, [field]: value }));
    setRecordsPage(1);
  };

  const handleRecordsSort = (field) => {
    setRecordsSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ field }) => {
    if (recordsSortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return recordsSortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  const StatusBadge = ({ status, type }) => {
    const configs = {
      spf: { found: 'bg-green-500/20 text-green-400', not_found: 'bg-red-500/20 text-red-400', error: 'bg-amber-500/20 text-amber-400' },
      dkim: { found: 'bg-green-500/20 text-green-400', not_found: 'bg-red-500/20 text-red-400', error: 'bg-amber-500/20 text-amber-400' },
      dmarc: { found: 'bg-green-500/20 text-green-400', not_found: 'bg-red-500/20 text-red-400', error: 'bg-amber-500/20 text-amber-400' },
      ssl: { 
        valid: 'bg-green-500/20 text-green-400', 
        expiring: 'bg-amber-500/20 text-amber-400',
        expired: 'bg-red-500/20 text-red-400',
        invalid: 'bg-red-500/20 text-red-400',
        no_ssl: 'bg-slate-500/20 text-slate-400',
        not_scanned: 'bg-slate-500/20 text-slate-400'
      }
    };
    const config = configs[type] || {};
    const className = config[status] || 'bg-slate-500/20 text-slate-400';
    const label = status === 'found' ? 'OK' : status === 'not_found' ? 'No' : status;
    
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${className}`}>
        {label}
      </span>
    );
  };

  const ScoreGauge = ({ score }) => {
    const color = score >= 80 ? 'text-green-500' : score >= 50 ? 'text-amber-500' : 'text-red-500';
    const bg = score >= 80 ? 'bg-green-500' : score >= 50 ? 'bg-amber-500' : 'bg-red-500';
    return (
      <div className="flex items-center gap-3">
        <div className="w-24 h-3 bg-muted rounded-full overflow-hidden">
          <div className={`h-full ${bg}`} style={{ width: `${score}%` }} />
        </div>
        <span className={`text-lg font-bold ${color}`}>{score}/100</span>
      </div>
    );
  };

  if (!canRead) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">No tienes permisos para ver DNS</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  if (!source) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Dominio no encontrado</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          <button 
            onClick={() => navigate('/assets/dns')}
            className="mt-1 p-1 hover:bg-muted rounded"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <Globe className="h-8 w-8 text-primary" strokeWidth={1.5} />
              <div>
                <h1 className="page-title">{source.name}</h1>
                <p className="text-muted-foreground font-mono">{source.domain}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Status Card */}
        <Card className="nerve-card">
          <CardContent className="p-5">
            <div className="flex flex-wrap items-center gap-8">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Tipo</p>
                <span className={`badge-base flex items-center gap-1.5 ${source.source_type === 'external' ? 'badge-blue' : 'badge-purple'}`}>
                  <span className={`status-dot w-2 h-2 ${source.source_type === 'external' ? 'status-online' : 'status-offline'}`} />
                  {source.source_type === 'external' ? 'Externo' : 'Interno'}
                </span>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Activo</p>
                <span className={`badge-base flex items-center gap-1.5 ${source.is_active ? 'badge-green' : 'badge-red'}`}>
                  <span className={`status-dot w-2 h-2 ${source.is_active ? 'status-online' : 'status-offline'}`} />
                  {source.is_active ? 'Sí' : 'No'}
                </span>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Registros</p>
                <p className="text-lg font-medium text-primary">{source.last_scan_records || 0}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Ultimo Scan</p>
                <p className="text-lg font-medium">{source.last_scan_at ? formatDateOnlyARG(source.last_scan_at) : '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="nerve-card p-1 flex-wrap h-auto gap-1">
            <TabsTrigger value="general" className="flex items-center gap-2" data-testid="tab-general">
              <Settings className="h-4 w-4" />
              General
            </TabsTrigger>
            <TabsTrigger value="records" className="flex items-center gap-2" data-testid="tab-records">
              <FileText className="h-4 w-4" />
              Records
            </TabsTrigger>
            <TabsTrigger value="email" className="flex items-center gap-2" data-testid="tab-email">
              <Mail className="h-4 w-4" />
              Email Security
            </TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general" className="mt-4">
            <div className="grid grid-cols-2 gap-6">
              {/* Información General */}
              <Card className="card-subtle">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Globe className="h-4 w-4 text-primary" />
                    Informacion General
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground flex items-center gap-2">
                      <Globe className="h-4 w-4" /> Nombre
                    </span>
                    <span className="font-medium text-primary">{source.name}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground flex items-center gap-2">
                      <ExternalLink className="h-4 w-4" /> Dominio
                    </span>
                    <span className="font-mono">{source.domain}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground flex items-center gap-2">
                      <Lock className="h-4 w-4" /> Tipo
                    </span>
                    <span className={`badge ${source.source_type === 'external' ? 'badge-blue' : 'badge-purple'}`}>
                      {source.source_type}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground flex items-center gap-2">
                      <Settings className="h-4 w-4" /> DNS Server
                    </span>
                    <span className="font-mono">{source.dns_server || '-'}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Configuración */}
              <Card className="card-subtle">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Settings className="h-4 w-4 text-primary" />
                    Configuracion
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">Activo</span>
                    {source.is_active ? (
                      <span className="flex items-center gap-1 text-green-500"><CheckCircle className="h-4 w-4" /> Si</span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-500"><XCircle className="h-4 w-4" /> No</span>
                    )}
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">Check Subdominios</span>
                    {source.check_subdomains ? (
                      <span className="flex items-center gap-1 text-green-500"><CheckCircle className="h-4 w-4" /> Si</span>
                    ) : (
                      <span className="flex items-center gap-1 text-muted-foreground"><XCircle className="h-4 w-4" /> No</span>
                    )}
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">Check Email Security</span>
                    {source.check_email_security ? (
                      <span className="flex items-center gap-1 text-green-500"><CheckCircle className="h-4 w-4" /> Si</span>
                    ) : (
                      <span className="flex items-center gap-1 text-muted-foreground"><XCircle className="h-4 w-4" /> No</span>
                    )}
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">Auto Scan SSL</span>
                    {source.auto_scan_ssl ? (
                      <span className="flex items-center gap-1 text-green-500"><CheckCircle className="h-4 w-4" /> Si</span>
                    ) : (
                      <span className="flex items-center gap-1 text-muted-foreground"><XCircle className="h-4 w-4" /> No</span>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Fechas */}
              <Card className="card-subtle col-span-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" />
                    Informacion de Escaneo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">Ultimo Scan</Label>
                      <p className="font-medium">{source.last_scan_at ? formatDateARG(source.last_scan_at) : '-'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Records Encontrados</Label>
                      <p className="font-medium text-primary">{source.last_scan_records || 0}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Creado</Label>
                      <p className="font-medium">{source.created_at ? formatDateARG(source.created_at) : '-'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Actualizado</Label>
                      <p className="font-medium">{source.updated_at ? formatDateARG(source.updated_at) : '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Records Tab */}
          <TabsContent value="records" className="mt-4">
            <Card className="card-subtle overflow-hidden">
              <CardContent className="p-0">
                {recordsLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="table-subtle">
                        <thead>
                          <tr>
                            <th 
                              className="cursor-pointer hover:bg-muted/50 select-none"
                              onClick={() => handleRecordsSort('record_fqdn')}
                            >
                              <div className="flex items-center gap-1">
                                FQDN
                                <SortIcon field="record_fqdn" />
                              </div>
                            </th>
                            <th 
                              className="cursor-pointer hover:bg-muted/50 select-none"
                              onClick={() => handleRecordsSort('record_type')}
                            >
                              <div className="flex items-center gap-1">
                                TIPO
                                <SortIcon field="record_type" />
                              </div>
                            </th>
                            <th>VALOR</th>
                            <th 
                              className="cursor-pointer hover:bg-muted/50 select-none text-right"
                              onClick={() => handleRecordsSort('ttl')}
                            >
                              <div className="flex items-center justify-end gap-1">
                                TTL
                                <SortIcon field="ttl" />
                              </div>
                            </th>
                            <th>PRIORIDAD</th>
                            <th>SSL</th>
                          </tr>
                          {/* Filters */}
                          <tr className="bg-muted/30">
                            <th className="py-2 px-4">
                              <Input
                                placeholder="Buscar..."
                                value={recordsFilters.search}
                                onChange={(e) => handleRecordsFilterChange('search', e.target.value)}
                                className="h-7 text-xs bg-background"
                                data-testid="filter-records-search"
                              />
                            </th>
                            <th className="py-2 px-4">
                              <select
                                value={recordsFilters.record_type}
                                onChange={(e) => handleRecordsFilterChange('record_type', e.target.value)}
                                className="h-7 w-full text-xs bg-background border border-border rounded px-2"
                              >
                                <option value="">Todos</option>
                                {recordTypes.map(t => (
                                  <option key={t} value={t}>{t}</option>
                                ))}
                              </select>
                            </th>
                            <th colSpan={4}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {records.length === 0 ? (
                            <tr>
                              <td colSpan={6}>
                                <div className="empty-state">
                                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                                  <p className="empty-state-text">No se encontraron records</p>
                                </div>
                              </td>
                            </tr>
                          ) : records.map((record) => (
                            <tr key={record.id} data-testid={`record-row-${record.id}`}>
                              <td className="font-mono text-sm text-primary">{record.record_fqdn}</td>
                              <td>
                                <span className="badge badge-outline">{record.record_type}</span>
                              </td>
                              <td className="font-mono text-xs text-muted-foreground max-w-xs truncate" title={record.record_value}>
                                {record.record_value}
                              </td>
                              <td className="text-right font-mono text-sm">{record.ttl || '-'}</td>
                              <td className="text-center">{record.priority || '-'}</td>
                              <td>
                                <StatusBadge status={record.ssl_status} type="ssl" />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                      <div className="text-sm text-muted-foreground">
                        {recordsTotal > 0 ? `${(recordsPage - 1) * 50 + 1} - ${Math.min(recordsPage * 50, recordsTotal)} de ${recordsTotal}` : '0 registros'}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setRecordsPage(p => Math.max(1, p - 1))}
                          disabled={recordsPage === 1}
                          className="h-8 w-8 p-0"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm text-muted-foreground">{recordsPage} / {recordsTotalPages || 1}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setRecordsPage(p => Math.min(recordsTotalPages, p + 1))}
                          disabled={recordsPage === recordsTotalPages || recordsTotalPages === 0}
                          className="h-8 w-8 p-0"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Security Tab */}
          <TabsContent value="email" className="mt-4">
            {emailLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : !emailSecurity ? (
              <Card className="card-subtle">
                <CardContent className="py-12">
                  <div className="empty-state">
                    <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="empty-state-text">No hay datos de seguridad de email para este dominio</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-2 gap-6">
                {/* Score Card */}
                <Card className="card-subtle col-span-2">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-primary" />
                        Puntuacion de Seguridad
                      </span>
                      <ScoreGauge score={emailSecurity.overall_score || 0} />
                    </CardTitle>
                  </CardHeader>
                  {emailSecurity.checked_at && (
                    <CardContent className="pt-0">
                      <p className="text-xs text-muted-foreground">
                        Ultimo chequeo: {formatDateARG(emailSecurity.checked_at)}
                      </p>
                    </CardContent>
                  )}
                </Card>

                {/* SPF */}
                <Card className="card-subtle">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-primary" />
                        SPF (Sender Policy Framework)
                      </span>
                      <StatusBadge status={emailSecurity.spf_status} type="spf" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {emailSecurity.spf_record && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Registro</Label>
                        <p className="font-mono text-xs bg-muted/30 p-2 rounded break-all">{emailSecurity.spf_record}</p>
                      </div>
                    )}
                    {emailSecurity.spf_policy && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Politica</Label>
                        <p className="font-medium">{emailSecurity.spf_policy}</p>
                      </div>
                    )}
                    {emailSecurity.spf_status === 'not_found' && (
                      <p className="text-amber-500 text-sm flex items-center gap-1">
                        <AlertTriangle className="h-4 w-4" />
                        No se encontro registro SPF
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* DKIM */}
                <Card className="card-subtle">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary" />
                        DKIM (DomainKeys)
                      </span>
                      <StatusBadge status={emailSecurity.dkim_status} type="dkim" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {emailSecurity.dkim_selector && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Selector</Label>
                        <p className="font-mono">{emailSecurity.dkim_selector}</p>
                      </div>
                    )}
                    {emailSecurity.dkim_record && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Registro</Label>
                        <p className="font-mono text-xs bg-muted/30 p-2 rounded break-all">{emailSecurity.dkim_record}</p>
                      </div>
                    )}
                    {emailSecurity.dkim_status === 'not_found' && (
                      <p className="text-amber-500 text-sm flex items-center gap-1">
                        <AlertTriangle className="h-4 w-4" />
                        No se encontro registro DKIM
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* DMARC */}
                <Card className="card-subtle">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Lock className="h-4 w-4 text-primary" />
                        DMARC
                      </span>
                      <StatusBadge status={emailSecurity.dmarc_status} type="dmarc" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {emailSecurity.dmarc_record && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Registro</Label>
                        <p className="font-mono text-xs bg-muted/30 p-2 rounded break-all">{emailSecurity.dmarc_record}</p>
                      </div>
                    )}
                    {emailSecurity.dmarc_policy && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Politica</Label>
                        <p className="font-medium">{emailSecurity.dmarc_policy}</p>
                      </div>
                    )}
                    {emailSecurity.dmarc_status === 'not_found' && (
                      <p className="text-amber-500 text-sm flex items-center gap-1">
                        <AlertTriangle className="h-4 w-4" />
                        No se encontro registro DMARC
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Recomendaciones */}
                {emailSecurity.recommendations && emailSecurity.recommendations.length > 0 && (
                  <Card className="card-subtle">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2 text-amber-500">
                        <AlertTriangle className="h-4 w-4" />
                        Recomendaciones
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {emailSecurity.recommendations.map((rec, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <span className="text-amber-500 mt-1">•</span>
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default DNSDetail;
